import {
  ehPorAcoes,
  ENQUADRAMENTOS,
  NOME_ENQUADRAMENTO,
  NOME_TIPO_SOCIETARIO,
  type Pessoa,
} from '@social/shared';
import { Campo, Entrada, Selecao } from '../componentes/ui';
import {
  SOCIOS_MINIMOS,
  TIPOS_OFERECIDOS,
  type RascunhoSociedade,
  type TipoOferecido,
} from './sociedade-nova-comum';
import { SecaoAdministradores, SecaoSocios } from './sociedade-nova-partes';
import { SecaoAcoes } from './sociedade-acoes';
import { SecaoFiliais } from './sociedade-filiais';
import { LinhaCheia, Secao } from './sociedade-nova-secao';

/**
 * Os campos da sociedade — os MESMOS na constituição e na alteração.
 *
 * Extraídos quando a alteração contratual chegou. A alternativa era duplicar
 * 130 linhas de formulário na segunda tela, e a consequência seria certa: um
 * campo novo entraria num caminho e não no outro, e a alteração passaria a
 * gravar em silêncio um dado a menos do que o cadastro grava.
 *
 * O que NÃO vem para cá é o que distingue as duas telas — título, verbo do
 * botão, para onde volta, e a data do instrumento, que só a alteração tem.
 * Componente compartilhado é para o que é igual; o que difere fica em cima.
 */
export function CamposDaSociedade({
  rascunho: r,
  mudar,
  pessoas,
  capital,
  valorNominal,
}: {
  rascunho: RascunhoSociedade;
  mudar: (p: Partial<RascunhoSociedade>) => void;
  pessoas: Pessoa[];
  capital: number;
  valorNominal: number;
}): JSX.Element {
  return (
    <>
      <Secao
        titulo="Identificação"
        descricao="Só aparecem os tipos cujo ato de constituição o gerador monta. A S.A. sai como estatuto; os demais, como contrato social."
      >
        <Campo rotulo="Tipo societário" id="tipo" obrigatorio>
          <Selecao
            id="tipo"
            value={r.tipo}
            onChange={(e) => mudar(trocaDeTipo(e.target.value as TipoOferecido))}
          >
            {TIPOS_OFERECIDOS.map((t) => (
              <option key={t} value={t}>
                {NOME_TIPO_SOCIETARIO[t]}
              </option>
            ))}
          </Selecao>
        </Campo>

        <Campo rotulo="Enquadramento" id="enquadramento" obrigatorio>
          <Selecao
            id="enquadramento"
            value={r.enquadramento}
            onChange={(e) =>
              mudar({
                enquadramento: e.target.value as RascunhoSociedade['enquadramento'],
              })
            }
          >
            {ENQUADRAMENTOS.map((e) => (
              <option key={e} value={e}>
                {NOME_ENQUADRAMENTO[e]}
              </option>
            ))}
          </Selecao>
        </Campo>

        <LinhaCheia>
          <Campo rotulo="Nome empresarial" id="nomeEmpresarial" obrigatorio>
            <Entrada
              id="nomeEmpresarial"
              value={r.nomeEmpresarial}
              onChange={(e) => mudar({ nomeEmpresarial: e.target.value })}
            />
          </Campo>
        </LinhaCheia>

        <Campo rotulo="Nome fantasia" id="nomeFantasia" dica="Opcional">
          <Entrada
            id="nomeFantasia"
            value={r.nomeFantasia}
            onChange={(e) => mudar({ nomeFantasia: e.target.value })}
          />
        </Campo>

        <Campo rotulo="Início das atividades" id="inicioAtividades" obrigatorio>
          <Entrada
            id="inicioAtividades"
            type="date"
            value={r.inicioAtividades}
            onChange={(e) => mudar({ inicioAtividades: e.target.value })}
          />
        </Campo>
      </Secao>

      <Secao titulo="Objeto social">
        <LinhaCheia>
          <Campo
            rotulo="Objeto"
            id="objetoSocial"
            obrigatorio
            dica="O que a sociedade faz, como sai no contrato — em minúsculas, sem ponto final."
          >
            <textarea
              id="objetoSocial"
              rows={3}
              value={r.objetoSocial}
              onChange={(e) => mudar({ objetoSocial: e.target.value })}
              className="border-tinta-100 bg-campo text-tinta-900 placeholder:text-tinta-500 w-full rounded-lg border px-3 py-2 text-sm"
            />
          </Campo>
        </LinhaCheia>

        <Campo rotulo="CNAE principal" id="cnae" obrigatorio dica="Só números">
          <Entrada
            id="cnae"
            inputMode="numeric"
            maxLength={7}
            value={r.atividades[0]?.cnae ?? ''}
            onChange={(e) =>
              mudar({
                atividades: [
                  {
                    cnae: e.target.value,
                    descricao: r.atividades[0]?.descricao ?? '',
                    principal: true,
                  },
                ],
              })
            }
          />
        </Campo>

        <Campo rotulo="Descrição do CNAE" id="cnaeDescricao" obrigatorio>
          <Entrada
            id="cnaeDescricao"
            value={r.atividades[0]?.descricao ?? ''}
            onChange={(e) =>
              mudar({
                atividades: [
                  {
                    cnae: r.atividades[0]?.cnae ?? '',
                    descricao: e.target.value,
                    principal: true,
                  },
                ],
              })
            }
          />
        </Campo>
      </Secao>

      <SecaoSede rascunho={r} mudar={mudar} />

      <SecaoSocios
        rascunho={r}
        mudar={mudar}
        pessoas={pessoas}
        capital={capital}
        valorNominal={valorNominal}
        minimo={SOCIOS_MINIMOS[r.tipo]}
      />

      {/* Antes da administração: o regime define se há Conselho, e conselho
          é órgão de administração. */}
      <SecaoAcoes rascunho={r} mudar={mudar} />

      <SecaoAdministradores rascunho={r} mudar={mudar} pessoas={pessoas} />

      <SecaoFiliais rascunho={r} mudar={mudar} />
    </>
  );
}

/**
 * Trocar o tipo cria ou destrói o regime de ações junto.
 *
 * Sem isto, escolher S.A. deixaria a seção do estatuto invisível (ela some
 * quando `acoes` é ausente) e a companhia sairia com os padrões da lei sem
 * ninguém ter visto a pergunta. E o caminho de volta é igualmente necessário:
 * um regime de ações sobrevivendo numa limitada é dado morto que o laudo
 * denuncia e que o próximo leitor tomaria por verdade.
 *
 * Os padrões são os da própria lei — ver `SecaoAcoes`.
 */
function trocaDeTipo(tipo: TipoOferecido): Partial<RascunhoSociedade> {
  if (!ehPorAcoes(tipo)) return { tipo, acoes: undefined };
  return {
    tipo,
    acoes: {
      aberta: false,
      capitalAutorizado: '',
      conselhoAdministracao: false,
      conselheiros: '3',
      conselhoFiscalPermanente: false,
      dividendoMinimo: '25,00',
      vantagemPreferenciais: '',
    },
  };
}

function SecaoSede({
  rascunho: r,
  mudar,
}: {
  rascunho: RascunhoSociedade;
  mudar: (p: Partial<RascunhoSociedade>) => void;
}): JSX.Element {
  const sede = (parcial: Partial<RascunhoSociedade['sede']>) =>
    mudar({ sede: { ...r.sede, ...parcial } });

  return (
    <Secao titulo="Sede e foro">
      <LinhaCheia>
        <Campo rotulo="Logradouro" id="sede.logradouro" obrigatorio>
          <Entrada
            id="sede.logradouro"
            value={r.sede.logradouro}
            onChange={(e) => sede({ logradouro: e.target.value })}
          />
        </Campo>
      </LinhaCheia>

      <Campo rotulo="Número" id="sede.numero" obrigatorio>
        <Entrada
          id="sede.numero"
          value={r.sede.numero}
          onChange={(e) => sede({ numero: e.target.value })}
        />
      </Campo>

      <Campo rotulo="Bairro" id="sede.bairro" obrigatorio>
        <Entrada
          id="sede.bairro"
          value={r.sede.bairro}
          onChange={(e) => sede({ bairro: e.target.value })}
        />
      </Campo>

      <Campo rotulo="Município" id="sede.municipio" obrigatorio>
        <Entrada
          id="sede.municipio"
          value={r.sede.municipio}
          onChange={(e) => {
            // O foro segue o município da sede por padrão, que é o caso
            // esmagadoramente comum — e continua editável logo abaixo.
            sede({ municipio: e.target.value });
            if (!r.foroComarca || r.foroComarca === r.sede.municipio) {
              mudar({ foroComarca: e.target.value });
            }
          }}
        />
      </Campo>

      <Campo rotulo="UF" id="sede.uf" obrigatorio>
        <Entrada
          id="sede.uf"
          maxLength={2}
          value={r.sede.uf}
          onChange={(e) => {
            sede({ uf: e.target.value });
            if (!r.foroUf || r.foroUf === r.sede.uf) mudar({ foroUf: e.target.value });
          }}
        />
      </Campo>

      <Campo rotulo="CEP" id="sede.cep" obrigatorio dica="Só números">
        <Entrada
          id="sede.cep"
          inputMode="numeric"
          maxLength={8}
          value={r.sede.cep}
          onChange={(e) => sede({ cep: e.target.value })}
        />
      </Campo>

      <Campo
        rotulo="Foro (comarca)"
        id="foroComarca"
        obrigatorio
        dica="Acompanha o município da sede, e pode ser outro"
      >
        <Entrada
          id="foroComarca"
          value={r.foroComarca}
          onChange={(e) => mudar({ foroComarca: e.target.value })}
        />
      </Campo>

      <Campo rotulo="UF do foro" id="foroUf" obrigatorio>
        <Entrada
          id="foroUf"
          maxLength={2}
          value={r.foroUf}
          onChange={(e) => mudar({ foroUf: e.target.value })}
        />
      </Campo>
    </Secao>
  );
}
